import { ARENA } from '../../../config/constants.js';

/**
 * The Manifold — a second body (Act III-4, ACT3-BIBLE §3).
 *
 * A song that is carried gets carried wrong, and the wrongness is the point.
 * The fight gives the player a second avatar that mirrors their movement across
 * an axis through the arena's centre. Both bodies share ONE health pool, so you
 * are playing one control scheme in two places and cannot protect them both.
 *
 * ### What this is, and what it deliberately isn't
 *
 * The bible (§9) budgets a full second `Player` — camera framing, collision,
 * HUD, pickups, Aria's cascade origin, Cairn's debt bank all auditing clean —
 * as the act's largest engine touch, and gates it behind a one-player audit.
 * That audit's honest answer is that a real second Player touches almost every
 * system in the game for one boss. So the mirror here is a *body*, not a
 * Player: it has a position and a radius, it takes boss bullets into the
 * player's HP pool, and it deals the player's damage in a ring around itself.
 * Everything else about the player — pickups, dash charges, champion state —
 * stays singular and stays where it is.
 *
 * That's a smaller thing than the bible describes, and it's the right smaller
 * thing: the dilemma the fight is *for* ("you cannot protect them both", "your
 * own damage is coming from somewhere you aren't") is fully present, and none
 * of the systems that assume one player have to change at all.
 *
 * ### Escalation and the assist
 *
 * The mirror axis drifts continuously (`axisDriftPerPhase`), so the mapping
 * stops being intuitive as the fight goes on, and at `divisiPhase` a third body
 * splits off on a telegraph (Divisi).
 *
 * Mirrored control is exactly the mechanic that hurts most under motor or
 * spatial-processing difficulty, so the assist the bible calls a shipping
 * requirement ships with it: with `assist` set, the second body follows the
 * player instead of mirroring them, at reduced damage. See SettingsMenu.
 */
export class ManifoldSystem {
  constructor(eventBus, config, { player, combat, assist = false }) {
    this.eventBus = eventBus;
    this.config = config;
    this.player = player;
    this.combat = combat;
    this.assist = assist;
    this.bodies = [];
    this._axis = 0; // radians; the line the mirror reflects across
    this._damageTimer = 0;
    this._hurtTimer = 0;
    this._divisi = false;
  }

  get _centerX() { return ARENA.x + ARENA.width / 2; }
  get _centerY() { return ARENA.y + ARENA.height / 2; }

  /** Reflects (x, y) across the line through arena centre at angle `this._axis`. */
  _reflect(x, y, axis) {
    const dx = x - this._centerX;
    const dy = y - this._centerY;
    const cos = Math.cos(2 * axis);
    const sin = Math.sin(2 * axis);
    return {
      x: this._centerX + dx * cos + dy * sin,
      y: this._centerY + dx * sin - dy * cos,
    };
  }

  update(dt, boss, bulletPool) {
    if (!boss || boss.isDying) {
      this.bodies = [];
      return;
    }
    const phase = boss.phase;
    this._axis += this.config.axisDriftPerPhase * phase * dt;
    this._divisi = phase >= (this.config.divisiPhase ?? 99);

    const count = (this.config.bodies - 1) + (this._divisi ? 1 : 0);
    this.bodies = [];
    for (let i = 0; i < count; i++) {
      if (this.assist) {
        // Assist: the body trails the player at a fixed offset instead of
        // mirroring — still a second thing to keep alive, without asking the
        // hands to invert.
        const angle = this._axis + (i / count) * Math.PI * 2;
        this.bodies.push({
          x: this.player.x + Math.cos(angle) * this.config.assistOffset,
          y: this.player.y + Math.sin(angle) * this.config.assistOffset,
        });
      } else {
        // Each extra body is reflected across a different axis, so Divisi
        // reads as the arena carrying three of you rather than two overlapping.
        this.bodies.push(this._reflect(this.player.x, this.player.y, this._axis + (i * Math.PI) / 3));
      }
    }
    for (const body of this.bodies) {
      body.x = Math.min(ARENA.x + ARENA.width, Math.max(ARENA.x, body.x));
      body.y = Math.min(ARENA.y + ARENA.height, Math.max(ARENA.y, body.y));
    }

    this._takeHits(dt, bulletPool);
    this._dealDamage(dt, boss);
  }

  /**
   * Boss bullets that strike a mirror body damage the shared pool. Checked here
   * rather than in SpawningSystem's own collision pass so that pass keeps
   * knowing about exactly one player, which is the whole point of the design
   * note above.
   */
  _takeHits(dt, bulletPool) {
    this._hurtTimer -= dt;
    if (!bulletPool || this._hurtTimer > 0) return;
    const radius = this.config.bodyRadius;
    for (const body of this.bodies) {
      let struck = null;
      bulletPool.forEachActive((bullet) => {
        if (struck || !bullet.active) return;
        if (Math.hypot(bullet.x - body.x, bullet.y - body.y) < bullet.radius + radius) struck = bullet;
      });
      if (!struck) continue;
      struck.active = false;
      // Routed through the player's own takeDamage so mitigation, Aegis plates,
      // Cairn's Debt and the death recap all behave exactly as they do for a
      // hit on the real body — the pool is shared, so the rules must be too.
      this.player.takeDamage(struck.damage * this.config.damageTaken, 'The Manifold');
      this._hurtTimer = 0.35;
      this.eventBus.emit('manifold:struck', { x: body.x, y: body.y });
      return;
    }
  }

  /** A mirror body deals the player's damage in a ring around itself. */
  _dealDamage(dt, boss) {
    if (!this.combat) return;
    this._damageTimer -= dt;
    if (this._damageTimer > 0) return;
    this._damageTimer = this.config.damageInterval;
    const scale = this.assist ? this.config.assistDamageScale : 1;
    for (const body of this.bodies) {
      if (Math.hypot(boss.x - body.x, boss.y - body.y) > this.config.damageRadius + boss.radius) continue;
      this.combat.applyAreaDamage({
        x: body.x,
        y: body.y,
        radius: this.config.damageRadius,
        damage: this.config.damagePerTick * scale,
        player: this.player,
      });
    }
  }

  getVisual() {
    return { bodies: this.bodies, radius: this.config.bodyRadius, assist: this.assist, divisi: this._divisi };
  }

  dispose() {
    this.bodies = [];
  }
}
